// @ts-strict-ignore
import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexContainer } from '@components/_rolex/Container'
import { RolexImage } from '@components/_rolex/Image'
import { RolexModelAvailibility } from '@components/_rolex/ModelAvailibility'
import RolexPage from '@components/_rolex/Page'
import { RolexProductProperties } from '@components/_rolex/ProductProperties'
import { RolexTextBlock } from '@components/_rolex/TextBlock'
import { RolexContactFormModule } from '@components/_rolex/_contact-form/Module'
import { RolexProductPageHero } from '@components/_rolex/product-page-hero'
import { RolexPushBanner } from '@components/_rolex/push-banner-grid/PushBanner'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { Heading } from '@components/rolex-ui/Heading'
import { SchemaOrg } from '@components/schema-org'
import { cn } from '@lib/cn'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse, getCatchAllRolexProductPaths, getPageByCodename } from '@lib/kontent'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { RolexCollectionPage } from '@lib/kontent/models/RolexCollectionPage'
import { RolexProductPageV72024 } from '@lib/kontent/models/RolexProductPageV72024'
import { RolexSendUsAMessage } from '@lib/kontent/models/RolexSendUsAMessage'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { FC, useEffect } from 'react'

type Props = SingleItemResponse<RolexProductPageV72024> & {
  familyInfo?: SingleItemResponse<RolexCollectionPage>
  fid: string
  contactPageContent: SingleItemResponse<RolexSendUsAMessage>
}

const RolexDetail: FC<Props> = ({ item, familyInfo, fid, contactPageContent }) => {
  const { locale } = useRouter()
  const { t } = useTranslation(['rolex', 'navigation'])
  const keepExploringItems = familyInfo.item.elements.keep_exploring_items.value.map(
    (key) => familyInfo.modular_content[key].elements,
  )

  useEffect(() => {
    window['productModelPageRLX'] = `${fid};${item.elements.url_slug.value};`
  }, []) // eslint-disable-line

  return (
    <Page
      {...pickSeoProps(item.elements)}
      slug={`rolex/${fid}/${item.elements.url_slug.value}`}
      title={item.elements.seo_metadata__title.value}
      noindex={locale === 'de'}
    >
      <SchemaOrg variant="rolex-product" data={item} />
      <Script
        type="text/javascript"
        id="rlx-plaques"
        data-dak="3749a28a87220925506fa122a876c80e"
        src="https://static.rolex.com/rlx-plaques-v2/js/plaquespage.js"
        strategy="lazyOnload"
      />
      <RolexPage
        pageType="model page"
        productFamily={fid}
        productRMC={item.elements.url_slug.value}
      >
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.watches', { ns: 'rolex' }),
              href: `/rolex${t('rolex.watches', { ns: 'navigation' })}`,
            },
            {
              label: familyInfo.item.elements.collection_name.value,
              href: `/rolex/${familyInfo.item.elements.url_slug.value}`,
            },
          ]}
          currentPageTitle={item.elements.url_slug.value.toUpperCase()}
        />
        <RolexProductPageHero
          kenticoImage={item.elements.main_image}
          title={item.elements.title.value}
          description={item.elements.short_description.value}
          price={item.elements.price.value}
        />
        <RolexProductProperties {...item.elements} />
        <div className="bg-rolexLightBeige py-[60px] md:py-[90px]">
          <div className="mb-[30px] md:mb-[45px]">
            <RolexTextBlock
              title={item.elements.feature_1_title.value}
              description={item.elements.feature_1_text.value}
              headingTag="h2"
              spacing="large"
            />
            <RolexImage
              size="s"
              mobileImage={item.elements.feature_1_image}
              desktopImage={item.elements.feature_1_image}
              spacing="large"
            />
          </div>
          <div className="mb-[30px] md:mb-[45px]">
            <RolexTextBlock
              title={item.elements.feature_2_title.value}
              description={item.elements.feature_2_text.value}
              headingTag="h2"
              spacing="large"
            />
            <RolexImage
              size="s"
              mobileImage={item.elements.feature_2_image}
              desktopImage={item.elements.feature_2_image}
              spacing="large"
            />
          </div>
          <div className="mb-[30px] md:mb-[45px]">
            <RolexTextBlock
              title={item.elements.feature_3_title.value}
              description={item.elements.feature_3_text.value}
              headingTag="h2"
              spacing="large"
            />
            <RolexImage
              size="l"
              mobileImage={item.elements.feature_3_image}
              desktopImage={item.elements.feature_3_image}
              spacing="large"
            />
          </div>
          <RolexModelAvailibility image={item.elements.availability_image} />
          <RolexContainer className={cn('pb-[30px] pt-[60px]', 'md:pt-[90px]')}>
            <Heading variant="headline36" className="mb-5 text-rolexBrown">
              {t('contactUs')}
            </Heading>
            <RolexContactFormModule item={contactPageContent.item} backgroundColor="white" />
          </RolexContainer>
          <RolexContainer className="pt-[30px] md:pt-[60px]">
            <RolexPushBanner
              mobileImage={familyInfo.item.elements.mobile_image_817de15}
              desktopImage={familyInfo.item.elements.desktop_image_2856a45}
              pretitle={`Rolex ${familyInfo.item.elements.collection_name.value}`}
              title={familyInfo.item.elements.collection_pay_off.value}
              buttonLink={`/rolex/${familyInfo.item.elements.url_slug.value}`}
              buttonText={t('cta.moreDetails')}
              gridLayout="n1_column"
            />
          </RolexContainer>
        </div>
        <KeepExploring className="py-[30px] md:py-[60px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview, params }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const urlSlug = params.pid as string
    const content = await getPageByTypeAndUrlSlug<RolexProductPageV72024>({
      language,
      type: 'rolex_product_page_v7_2024',
      urlSlug,
      preview,
    })

    if (!content) {
      return {
        notFound: true,
      }
    }

    let familyInfo: SingleItemResponse<RolexCollectionPage> | null
    if (content.item.elements.rolex_family.value[0]) {
      let familySlug = content.item.elements.rolex_family.value[0].codename.replaceAll('_', '-')
      if (familySlug === 'family-1908') {
        familySlug = '1908'
      }

      familyInfo = await getPageByTypeAndUrlSlug<RolexCollectionPage>({
        type: 'rolex_collection_page',
        urlSlug: familySlug,
        language,
        preview,
      })
    }

    const contactPageContent = await getPageByCodename<RolexSendUsAMessage>({
      codename: 'rolex___send_us_a_message',
      lang: language,
      preview,
    })

    return {
      props: {
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
        ...content,
        familyInfo: familyInfo as SingleItemResponse<RolexCollectionPage>,
        fid: params.fid,
        contactPageContent,
      },
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = async () => {
  if (process.env.VERCEL_ENV !== 'production') {
    return { paths: [], fallback: 'blocking' }
  }

  const paths = await getCatchAllRolexProductPaths()
  return {
    fallback: false,
    paths,
  }
}

export default RolexDetail
